/**
 * RoundDesigner.tsx
 * Front-end for CapLedger Phase 3 scenario modeling.
 * Lets users simulate and apply financing rounds with live dilution charts.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { Loader2, Plus, TrendingUp, AlertTriangle } from 'lucide-react';
import { z } from 'zod';

const COLORS = ['#2563eb', '#10b981', '#f97316', '#a855f7', '#ec4899'];

type PoolTiming = 'pre' | 'post';

const roundSchema = z.object({
  roundLabel: z.string().min(1, 'Round label is required').max(100),
  roundType: z.enum(['SEED', 'SERIES_A', 'SERIES_B', 'NOTE', 'SAFE', 'OTHER']),
  preMoney: z.number().positive('Pre-money must be positive'),
  raiseAmount: z.number().positive('Raise amount must be positive'),
  poolPct: z.number().min(0).max(30),
  poolTiming: z.enum(['pre', 'post']),
  shareClassName: z.string().min(1).max(100),
});

interface SimulationResult {
  pre_money: number;
  raise_amount: number;
  post_money: number;
  current_fd: number;
  existing_pool_shares: number;
  pool_target_pct: number;
  pool_timing: PoolTiming;
  pool_topup_shares: number;
  new_investor_shares: number;
  price_per_share: number;
  post_money_fd: number;
  /** Reasons the round cannot be priced as specified, or is not what it looks like. */
  warnings: string[];
}

/**
 * Who owns what once the round closes.
 *
 * Derived here rather than read off the simulation because the simulation reports
 * share counts, and a founder decides on percentages. Existing holders are taken
 * net of the pool already outstanding, so "existing holders" means people rather
 * than people plus unissued options.
 */
function ownershipOf(s: SimulationResult) {
  const total = Number(s.post_money_fd) || 0;
  if (total <= 0) return { existing: 0, pool: 0, investor: 0 };
  const existingPool = Number(s.existing_pool_shares) || 0;
  return {
    existing: ((Number(s.current_fd) - existingPool) / total) * 100,
    pool: ((existingPool + Number(s.pool_topup_shares)) / total) * 100,
    investor: (Number(s.new_investor_shares) / total) * 100,
  };
}

interface RoundDesignerProps {
  companyId: string;
}

export default function RoundDesigner({ companyId }: RoundDesignerProps) {
  const [roundLabel, setRoundLabel] = useState('Series A');
  const [roundType, setRoundType] = useState<
    'SEED' | 'SERIES_A' | 'SERIES_B' | 'NOTE' | 'SAFE' | 'OTHER'
  >('SERIES_A');
  const [preMoney, setPreMoney] = useState('50000000');
  const [raiseAmount, setRaiseAmount] = useState('10000000');
  const [poolPct, setPoolPct] = useState(10);
  const [poolTiming, setPoolTiming] = useState<PoolTiming>('pre');
  const [shareClassName, setShareClassName] = useState('Series A Preferred');
  const [simData, setSimData] = useState<SimulationResult | null>(null);
  // The same round under the other timing, so the choice can be shown as a
  // consequence rather than as a term of art the founder is expected to know.
  const [altData, setAltData] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [currentRoundId, setCurrentRoundId] = useState<string | null>(null);
  // The funding_rounds deal this pricing came from, when arrived at via "Price
  // this round". Carried through Save so applying it can close that deal.
  const [sourceFundingRoundId, setSourceFundingRoundId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('round');

  useEffect(() => {
    // A deal named in the URL wins: someone pressed "Price this round" and
    // means that one, not whatever unapplied round happens to be newest.
    if (dealId) {
      loadFundingRoundDeal(dealId);
    } else {
      loadUnappliedRounds();
    }
  }, [companyId, dealId]);

  /**
   * Prefill from a funding_rounds deal — the record the Round Setup wizard
   * writes, which nothing could previously apply.
   *
   * Two mappings are not one-to-one and are the reason this is a function
   * rather than a spread:
   *   - the deal's raise lives in `target_amount`; pricing reads `raise_amount`,
   *     which the wizard never writes;
   *   - the deal's pool is `option_pool_increase`, already a fraction.
   *
   * `option_pool_timing` has no column on funding_rounds at all, so it is left
   * at the component's default of pre-money — the conventional reading of an
   * unqualified pool. That is safe here only because the Designer shows both
   * timings side by side before anything is saved, so the choice stays visible.
   */
  async function loadFundingRoundDeal(id: string) {
    try {
      const { data, error } = await supabase
        .from('funding_rounds')
        .select('*')
        .eq('id', id)
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        toast.error('That round could not be found for this company.');
        return;
      }

      setSourceFundingRoundId(data.id);
      setRoundLabel(data.round_label || data.name || '');

      const known = ['SEED', 'SERIES_A', 'SERIES_B', 'NOTE', 'SAFE', 'OTHER'];
      if (data.round_type && known.includes(data.round_type)) {
        setRoundType(data.round_type as typeof roundType);
      }

      if (data.pre_money) setPreMoney(String(data.pre_money));

      const raise = data.target_amount ?? data.raise_amount;
      if (raise) setRaiseAmount(String(raise));

      const pool = data.option_pool_increase ?? data.option_pool_refresh_pct;
      if (pool) setPoolPct(pool * 100);

      toast.info('Loaded from your round setup. Simulate to price it.');
    } catch (err) {
      console.error('Error loading funding round deal:', err);
      toast.error('Could not load that round.');
    }
  }

  async function loadUnappliedRounds() {
    try {
      const { data, error } = await supabase
        .from('financing_rounds')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_applied', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setCurrentRoundId(data.id);
        setRoundLabel(data.round_label);
        setRoundType(data.round_type as any);
        setPreMoney(data.pre_money.toString());
        setRaiseAmount(data.raise_amount.toString());
        setPoolPct((data.option_pool_refresh_pct || 0) * 100);
        setPoolTiming((data.option_pool_timing as PoolTiming) || 'pre');
      }
    } catch (err) {
      console.error('Error loading unapplied rounds:', err);
    }
  }

  async function handleSimulate() {
    try {
      setLoading(true);

      // Validate inputs
      const validated = roundSchema.parse({
        roundLabel,
        roundType,
        preMoney: parseFloat(preMoney),
        raiseAmount: parseFloat(raiseAmount),
        poolPct,
        poolTiming,
        shareClassName,
      });

      const other: PoolTiming = validated.poolTiming === 'pre' ? 'post' : 'pre';
      const run = (timing: PoolTiming) =>
        supabase.rpc('simulate_round', {
          p_company_id: companyId,
          p_pre_money: validated.preMoney,
          p_raise_amount: validated.raiseAmount,
          p_pool_target_post_pct: validated.poolPct / 100,
          // Was omitted entirely, so every preview was pre-money whatever the
          // founder intended.
          p_pool_timing: timing,
        });

      // Both timings, so the alternative can be shown as a number rather than
      // left as a term the founder has to already understand.
      const [chosen, alternative] = await Promise.all([run(validated.poolTiming), run(other)]);

      if (chosen.error) throw chosen.error;

      if (chosen.data && typeof chosen.data === 'object') {
        setSimData(chosen.data as unknown as SimulationResult);
        setAltData(
          !alternative.error && alternative.data && typeof alternative.data === 'object'
            ? (alternative.data as unknown as SimulationResult)
            : null
        );
        toast.success('Simulation complete');
      } else {
        throw new Error('Invalid simulation response');
      }
    } catch (err: any) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      } else {
        toast.error(err.message || 'Simulation failed');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveRound() {
    try {
      if (!simData) {
        toast.error('Please simulate first');
        return;
      }

      // Validate inputs
      const validated = roundSchema.parse({
        roundLabel,
        roundType,
        preMoney: parseFloat(preMoney),
        raiseAmount: parseFloat(raiseAmount),
        poolPct,
        poolTiming,
        shareClassName,
      });

      // Create or update financing round record
      const roundData = {
        company_id: companyId,
        round_label: validated.roundLabel,
        round_type: validated.roundType,
        pre_money: validated.preMoney,
        raise_amount: validated.raiseAmount,
        option_pool_refresh_pct: validated.poolPct / 100,
        // Persisted, so apply_round writes the round that was previewed. Without
        // this the choice lived only in the browser and the ledger always got a
        // pre-money pool.
        option_pool_timing: validated.poolTiming,
        is_applied: false,
        // Carried so applying this round can close the deal it came from. That
        // is what finally writes funding_rounds.is_applied and closed_at, which
        // the investor projection and timeline have always read and nothing
        // ever set.
        source_funding_round_id: sourceFundingRoundId,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      };

      if (currentRoundId) {
        const { error } = await supabase
          .from('financing_rounds')
          .update(roundData)
          .eq('id', currentRoundId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('financing_rounds')
          .insert(roundData)
          .select()
          .single();

        if (error) throw error;
        setCurrentRoundId(data.id);
      }

      toast.success('Round saved successfully');
    } catch (err: any) {
      if (err instanceof z.ZodError) {
        toast.error(err.errors[0].message);
      } else {
        toast.error(err.message || 'Failed to save round');
      }
    }
  }

  async function handleApply() {
    try {
      if (!simData) {
        toast.error('Please simulate first');
        return;
      }

      if (!currentRoundId) {
        toast.error('Please save the round first');
        return;
      }

      setApplying(true);

      const { data, error } = await supabase.rpc('apply_round', {
        p_round_id: currentRoundId,
        p_share_class_name: shareClassName,
      });

      if (error) throw error;

      toast.success('Round applied successfully! Ledger events created.');

      // Reset form
      setSimData(null);
      setAltData(null);
      setCurrentRoundId(null);
      // Cleared with the rest: the deal is closed now, and leaving it set would
      // attach the next round designed here to a round that has already been
      // applied.
      setSourceFundingRoundId(null);
      setRoundLabel('');
      setPreMoney('50000000');
      setRaiseAmount('10000000');
      setPoolPct(10);
      setPoolTiming('pre');

      // Reload to check for other unapplied rounds
      await loadUnappliedRounds();
    } catch (err: any) {
      toast.error(err.message || 'Failed to apply round');
    } finally {
      setApplying(false);
    }
  }

  // Split the existing pool out of "existing holders" rather than lumping it in:
  // unissued options are not a shareholder, and a founder reading this slice as
  // "us" would overstate what they hold.
  const chartData = simData
    ? [
        {
          name: 'Existing Holders',
          value: Number(simData.current_fd) - Number(simData.existing_pool_shares || 0),
        },
        { name: 'New Investors', value: Number(simData.new_investor_shares) },
        {
          name: 'Option Pool',
          value: Number(simData.existing_pool_shares || 0) + Number(simData.pool_topup_shares),
        },
      ].filter((item) => item.value > 0)
    : [];

  const own = simData ? ownershipOf(simData) : null;
  const altOwn = altData ? ownershipOf(altData) : null;
  const warnings = Array.isArray(simData?.warnings) ? simData.warnings : [];

  /**
   * `simulate_round` returns zeros when a round cannot be priced at all — no
   * shares on issue, or a raise and pool that between them account for the whole
   * company. Those zeros are not a result and must not be rendered as one.
   *
   * Shown as a result they actively mislead: an unpriceable round reports the
   * existing holders at 100% (nothing was issued), so the comparison panel
   * announced that it kept them "91.8 points better off" than the round that
   * works. That reads as an endorsement of the round the engine just refused.
   */
  const priceable = (s: SimulationResult | null) =>
    !!s && Number(s.price_per_share) > 0 && Number(s.post_money_fd) > 0;
  const canPrice = priceable(simData);
  const canCompare = canPrice && priceable(altData);

  /**
   * Round before differencing, so the gap equals what a founder gets by
   * subtracting the two percentages on screen. Differencing the exact values and
   * rounding afterwards printed "1.7 points" beside 75.0% and 73.4%.
   */
  const oneDp = (n: number) => Math.round(n * 10) / 10;

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Round Designer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Round Details */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="roundLabel">Round Label</Label>
            <Input
              id="roundLabel"
              value={roundLabel}
              onChange={(e) => setRoundLabel(e.target.value.slice(0, 100))}
              placeholder="e.g., Series A"
            />
          </div>
          <div>
            <Label htmlFor="roundType">Round Type</Label>
            <Select value={roundType} onValueChange={(v: any) => setRoundType(v)}>
              <SelectTrigger id="roundType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SEED">Seed</SelectItem>
                <SelectItem value="SERIES_A">Series A</SelectItem>
                <SelectItem value="SERIES_B">Series B</SelectItem>
                <SelectItem value="NOTE">Convertible Note</SelectItem>
                <SelectItem value="SAFE">SAFE</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Financial Parameters */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="preMoney">Pre-Money Valuation (R)</Label>
            <Input
              id="preMoney"
              type="number"
              value={preMoney}
              onChange={(e) => setPreMoney(e.target.value)}
              min="0"
            />
          </div>
          <div>
            <Label htmlFor="raiseAmount">Raise Amount (R)</Label>
            <Input
              id="raiseAmount"
              type="number"
              value={raiseAmount}
              onChange={(e) => setRaiseAmount(e.target.value)}
              min="0"
            />
          </div>
          <div>
            <Label htmlFor="shareClassName">Share Class Name</Label>
            <Input
              id="shareClassName"
              value={shareClassName}
              onChange={(e) => setShareClassName(e.target.value.slice(0, 100))}
              placeholder="e.g., Series A Preferred"
            />
          </div>
        </div>

        {/* Option Pool */}
        <div className="space-y-4 rounded-md border border-border p-4">
          <div>
            <Label htmlFor="poolSlider">Option Pool Target (%)</Label>
            <p className="text-xs text-muted-foreground mt-1">
              As a share of the company <em>after</em> the round — which is how a term sheet states
              it.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <Slider
                id="poolSlider"
                value={[poolPct]}
                onValueChange={(v) => setPoolPct(v[0])}
                min={0}
                max={30}
                step={1}
                className="flex-1"
              />
              <span className="w-12 text-sm font-medium">{poolPct}%</span>
            </div>
          </div>

          {poolPct > 0 && (
            <div>
              <Label htmlFor="poolTiming">When is the pool created?</Label>
              <Select
                value={poolTiming}
                onValueChange={(v: PoolTiming) => {
                  setPoolTiming(v);
                  // The showing figures were computed for the other choice.
                  setSimData(null);
                  setAltData(null);
                }}
              >
                <SelectTrigger id="poolTiming" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre">Before the round (pre-money) — you pay for it</SelectItem>
                  <SelectItem value="post">
                    After the round (post-money) — everyone pays for it
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                {poolTiming === 'pre'
                  ? 'The pool is carved out before the share price is struck, so existing holders ' +
                    'carry all of it and the investor still gets the percentage they negotiated. ' +
                    'This is the usual term, and the usual place a founder gives away more than ' +
                    'they expected.'
                  : 'The pool is created after the round, so it dilutes the new investor too. ' +
                    'Better for you than pre-money, and less common — worth checking the term ' +
                    'sheet says so.'}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleSimulate} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Simulate
          </Button>

          {/* Only offer to save or apply a round that can actually be priced.
              These appeared for an unpriceable one, and Apply would have raised. */}
          {simData && canPrice && (
            <>
              <Button onClick={handleSaveRound} variant="secondary">
                <Plus className="mr-2 h-4 w-4" />
                {currentRoundId ? 'Update Round' : 'Save Round'}
              </Button>

              <Button
                onClick={handleApply}
                variant="default"
                disabled={applying || !currentRoundId}
              >
                {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Apply to Ledger
              </Button>
            </>
          )}
        </div>

        {/* Warnings the engine returned. These were computed and discarded:
            simulate_round has always reported why a round cannot be priced, or is
            not the round it appears to be, and nothing displayed them. */}
        {simData && warnings.length > 0 && (
          <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <h3 className="text-sm font-semibold">
                {warnings.length === 1
                  ? 'One thing to check'
                  : `${warnings.length} things to check`}
              </h3>
            </div>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {warnings.map((w, i) => (
                <li key={i} className="flex gap-2">
                  <span aria-hidden="true">•</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What the other timing would cost, in the only unit that matters. */}
        {simData && canCompare && altOwn && own && poolPct > 0 && (
          <div className="rounded-md border border-border bg-muted/40 p-4 text-sm">
            <h3 className="font-semibold mb-2">The other choice</h3>
            <p className="text-muted-foreground">
              With the pool created{' '}
              <strong>{poolTiming === 'pre' ? 'after' : 'before'} the round</strong> instead,
              existing holders would end up with <strong>{altOwn.existing.toFixed(1)}%</strong>{' '}
              rather than <strong>{own.existing.toFixed(1)}%</strong>, at a share price of R
              {Number(altData!.price_per_share).toFixed(4)} rather than R
              {Number(simData.price_per_share).toFixed(4)}.
            </p>
            {Math.abs(oneDp(altOwn.existing) - oneDp(own.existing)) >= 0.05 && (
              <p className="mt-2">
                {oneDp(altOwn.existing) > oneDp(own.existing) ? (
                  <>
                    That is{' '}
                    <strong>
                      {(oneDp(altOwn.existing) - oneDp(own.existing)).toFixed(1)} points more
                    </strong>{' '}
                    of the company for existing holders. If the term sheet does not specify, it is
                    worth asking.
                  </>
                ) : (
                  <>
                    The current choice keeps existing holders{' '}
                    <strong>
                      {(oneDp(own.existing) - oneDp(altOwn.existing)).toFixed(1)} points
                    </strong>{' '}
                    better off.
                  </>
                )}
              </p>
            )}
          </div>
        )}

        {/* A round the engine refused is not a result. Say so instead of
            printing a price of zero and a cap table nobody would get. */}
        {simData && !canPrice && (
          <div className="pt-4 border-t text-sm text-muted-foreground">
            This round cannot be priced as specified, so there are no figures to show. Adjust the
            valuation, the raise or the pool above and simulate again.
          </div>
        )}

        {/* Simulation Results */}
        {simData && canPrice && (
          <div className="grid sm:grid-cols-2 gap-6 pt-4 border-t">
            <div>
              <h3 className="text-sm font-semibold mb-3">Key Metrics</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price per Share:</span>
                  <span className="font-medium">R{Number(simData.price_per_share).toFixed(4)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">New Investor Shares:</span>
                  <span className="font-medium">
                    {Math.round(simData.new_investor_shares).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Option Pool Top-up:</span>
                  <span className="font-medium">
                    {Math.round(simData.pool_topup_shares).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Post-Money Valuation:</span>
                  <span className="font-medium">
                    R{Number(simData.post_money).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Post-Money FD Shares:</span>
                  <span className="font-medium">
                    {Math.round(simData.post_money_fd).toLocaleString()}
                  </span>
                </div>

                {own && (
                  <div className="pt-3 mt-1 border-t space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Existing holders after:</span>
                      <span className="font-semibold">{own.existing.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">New investors after:</span>
                      <span className="font-medium">{own.investor.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Option pool after:</span>
                      <span className="font-medium">{own.pool.toFixed(1)}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      Pool created{' '}
                      {(simData.pool_timing ?? poolTiming) === 'pre' ? 'before' : 'after'} the
                      round.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Ownership Breakdown</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    labelLine={false}
                  >
                    {chartData.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => Math.round(value).toLocaleString()} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
